import { useState, useEffect, useCallback } from 'react';
import { getRoster, addRosterPerson, updateRosterPerson, deleteRosterPerson } from '../api/org-api';
import type { RosterPerson, RosterPersonInput } from '../org-types';
import { logger } from '@/utils/logger';

// ============================================================================
// useRoster — Hook for managing roster data
// ============================================================================

interface UseRosterReturn {
    people: RosterPerson[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
    addPerson: (person: RosterPersonInput) => Promise<string>;
    updatePerson: (personId: string, data: Partial<RosterPerson>) => Promise<void>;
    deletePerson: (personId: string) => Promise<void>;
}

export function useRoster(orgId: string | null): UseRosterReturn {
    const [people, setPeople] = useState<RosterPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is an explicit reload trigger for the roster query.
    useEffect(() => {
        if (!orgId) {
            setPeople([]);
            setError(null);
            setLoading(false);
            return;
        }

        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const roster = await getRoster(orgId);
                setPeople(roster.toSorted((a, b) => a.name.localeCompare(b.name)));
            } catch (error: unknown) {
                logger.error('Error loading roster:', error);
                setError('Failed to load roster');
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, [orgId, refreshKey]);

    const addPerson = useCallback(
        async (person: RosterPersonInput): Promise<string> => {
            if (!orgId) throw new Error('No organization loaded');
            const id = await addRosterPerson(orgId, person);
            refresh();
            return id;
        },
        [orgId, refresh]
    );

    const updatePerson = useCallback(
        async (personId: string, data: Partial<RosterPerson>): Promise<void> => {
            if (!orgId) throw new Error('No organization loaded');
            await updateRosterPerson(orgId, personId, data);
            // Optimistic update
            setPeople((prev) =>
                prev.map((p) => (p.id === personId ? { ...p, ...data, updatedAt: new Date() } : p))
            );
        },
        [orgId]
    );

    const deletePerson = useCallback(
        async (personId: string): Promise<void> => {
            if (!orgId) throw new Error('No organization loaded');
            await deleteRosterPerson(orgId, personId);
            // Optimistic removal
            setPeople((prev) => prev.filter((p) => p.id !== personId));
        },
        [orgId]
    );

    return { people, loading, error, refresh, addPerson, updatePerson, deletePerson };
}
